import GoogleIcon from "@assets/icons/google.svg?react";
import PreviousIcon from "@src/app/assets/icons/arrow-left.svg?react";
import NextIcon from "@src/app/assets/icons/arrow-right.svg?react";
import ClientOnly from "@src/app/components/ClientOnly/ClientOnly";
import SocialShare from "@src/app/components/SocialShare/SocialShare";
import viewportState from "@src/app/stores/viewport";
import type { BFFArticleCard } from "@src/app/types/bff";
import cx from "classnames";
import dayjs from "dayjs";
import {
  Fragment,
  type ReactElement,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import { useSnapshot } from "valtio";

import Button from "../Button/Button";
import Byline from "../Byline/Byline";
import Link from "../Link";

type Props = Readonly<{
  videoArticle: BFFArticleCard;
  selectedVideoIndex: number;
  totalVideos?: number;
  isAutoplay?: boolean;
  showIndicator?: boolean;
  titleClampLines?: number;
  onPreviousVideo?: () => void;
  onNextVideo?: () => void;
  onEndedVideo?: () => void;
  onPlayVideo?: () => void;
  onPausedVideo?: () => void;
  onErrorVideo?: (event: YT.OnErrorEvent) => void;
}>;

export default function VideoPlayer({
  videoArticle,
  selectedVideoIndex,
  totalVideos,
  // isAutoplay,
  showIndicator = true,
  titleClampLines,
  onNextVideo,
  onPreviousVideo,
  onEndedVideo,
  onPlayVideo,
  onPausedVideo,
  onErrorVideo,
}: Props): ReactElement {
  const { isMobileView, isTabletView, isMobileOrTabletDevice } =
    useSnapshot(viewportState);
  const isMobileOrTablet =
    isMobileView || isTabletView || isMobileOrTabletDevice;

  const currentVideoNumber = (selectedVideoIndex || 0) + 1;
  const aspectRatio = 16 / 9;

  // Refs
  const iframeRef = useRef<HTMLIFrameElement | null>(null);
  const playerRef = useRef<YT.Player | null>(null);
  const descriptionRef = useRef<HTMLParagraphElement | null>(null);

  // Create refs to hold latest callbacks
  const onEndedVideoRef = useRef(onEndedVideo);
  const onPlayVideoRef = useRef(onPlayVideo);
  const onPausedVideoRef = useRef(onPausedVideo);
  const onErrorVideoRef = useRef(onErrorVideo);

  // State Management
  const [isDescriptionClamped, setIsDescriptionClamped] = useState(false);
  const [isDescriptionExpanded, setIsDescriptionExpanded] = useState(false);

  const toggleDescriptionExpansion = () => {
    setIsDescriptionExpanded(!isDescriptionExpanded);
  };

  // Keep refs updated whenever the corresponding props change
  /** 🧩 Article State */
  const {
    urlPath: articlePath,
    description: articleDesc,
    created,
    youtubeUrl = "",
    youtubeInfo: {
      id: youtubeInfoId,
      description: youtubeDesc,
      publishedAt,
    } = {},
    authors = [],
    title,
  } = videoArticle;

  const toYoutubeId = (url: string): string => {
    const fromEmbed = url.split("/embed/")[1] || "";
    return fromEmbed.split("?")[0] || "";
  };

  // Combine sources for derived fields
  const youtubeId = youtubeInfoId || toYoutubeId(youtubeUrl);
  const description = articleDesc || youtubeDesc || "";
  const dateSource = created || publishedAt;

  // Format date/time safely
  // const publishedTime = dateSource ? dayjs(dateSource).format("HH:mm") : "";
  const publishedDate = dateSource
    ? dayjs(dateSource).format("D MMM YYYY")
    : "";

  /** ----------------------------------------------
   * 🎬 YouTube Player Lifecycle (useEffect)
   * ----------------------------------------------
   * Handles dynamic loading and setup of the YouTube IFrame API.
   * Creates a `YT.Player` instance once the API is available and
   * binds all relevant player event callbacks.
   *
   * Responsibilities:
   *  - Load YouTube API script (if not already loaded)
   *  - Initialize player for the current `youtubeId`
   *  - Respond to player events (play, pause, end, error)
   *  - Maintain `videoArticleUrl` for sharing
   * ---------------------------------------------- */
  useEffect(() => {
    onEndedVideoRef.current = onEndedVideo;
  }, [onEndedVideo]);
  useEffect(() => {
    onPlayVideoRef.current = onPlayVideo;
  }, [onPlayVideo]);
  useEffect(() => {
    onPausedVideoRef.current = onPausedVideo;
  }, [onPausedVideo]);

  const handlePlayerStateChange = useCallback(
    (event: YT.OnStateChangeEvent) => {
      switch (event.data) {
        case YT.PlayerState.UNSTARTED:
          console.log("Video unstarted");
          break;
        case YT.PlayerState.ENDED:
          console.log("Video ended");
          onEndedVideoRef.current?.();
          break;
        case YT.PlayerState.PLAYING:
          console.log("Video is playing!");
          onPlayVideoRef.current?.();
          break;
        case YT.PlayerState.PAUSED:
          console.log("Video is paused!");
          onPausedVideoRef.current?.();
          break;
        case YT.PlayerState.BUFFERING:
          console.log("Video is buffering");
          break;
        case YT.PlayerState.CUED:
          console.log("Video cued");
          break;
        default:
          console.log("Unknown state");
      }
    },
    [],
  );

  useEffect(() => {
    const loadYouTubeAPI = () => {
      console.log("Loading YouTube IFrame API");
      if (!("YT" in window)) {
        // Create a script element to load the YouTube IFrame API
        const script = document.createElement("script");
        script.src = "https://www.youtube.com/iframe_api";
        script.async = true;
        document.body.appendChild(script);
        // The API will call window.onYouTubeIframeAPIReady when loaded
      } else {
        // If the API is already loaded, initialize the player
        initializePlayer();
      }
    };

    const initializePlayer = () => {
      if (
        iframeRef.current &&
        typeof window.YT !== "undefined" &&
        typeof window.YT.Player === "function"
      ) {
        // Destroy previous instance if exists
        playerRef.current?.destroy();

        console.log("Initializing Player");
        // Create a new YouTube player instance
        playerRef.current = new window.YT.Player(iframeRef.current.id, {
          videoId: youtubeId,
          width: "100%",
          height: "100%",
          playerVars: {
            autoplay: 1,
            mute: 1,
            playsinline: 1,
            rel: 0,
            enablejsapi: 1,
          },
          events: {
            onReady: (event) => {
              // Play the video when the player is ready
              console.log("Player is ready. Playing video...");
              event.target.playVideo();

              // Add custom allow attributes to the injected iframe
              const iframe = iframeRef.current?.querySelector("iframe");
              iframe?.setAttribute(
                "allow",
                "accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share",
              );
            },
            onStateChange: handlePlayerStateChange,
            onError: (event) => {
              // Handle error state if needed
              console.error("Error occurred in YouTube player:", event.data);
              onErrorVideoRef.current?.(event);
            },
          },
        });
      }
    };

    // Set the callback for when the YouTube IFrame API is ready
    window.onYouTubeIframeAPIReady = () => {
      console.log("YouTube IFrame API is ready");
      initializePlayer();
    };

    // Load the YouTube IFrame API
    loadYouTubeAPI();

    return () => {
      // Cleanup: Destroy the player instance when the component unmounts or dependencies change
      if (playerRef.current) {
        console.log("Destroying YouTube Player instance");
        playerRef.current.destroy();
        playerRef.current = null;
      }
    };
  }, [articlePath, youtubeUrl, youtubeId, handlePlayerStateChange]);

  // Reset description expansion when article changes
  useEffect(() => {
    // Reset the description expansion state
    setIsDescriptionExpanded(false);
  }, [articlePath]);

  // Check if description is clamped
  useEffect(() => {
    if (descriptionRef.current) {
      const visibleHeight = descriptionRef.current.clientHeight;
      const scrollHeight = descriptionRef.current.scrollHeight;

      if (scrollHeight > visibleHeight) {
        setIsDescriptionClamped(true);
      } else {
        setIsDescriptionClamped(false);
      }
    }
  }, [description]);

  return (
    <div
      id="video-player"
      data-testid="video-player"
      className="bg-background-primary-on-dark px-3 py-4"
    >
      {/* Video IFrame or Placeholder */}
      {youtubeUrl ? (
        <div style={{ aspectRatio }}>
          <div ref={iframeRef} id="youtube-iframe" className="relative z-10" />
        </div>
      ) : (
        <div
          className="flex w-full items-center justify-center bg-[#282828] text-[22px] font-medium text-[#EEEEEE]"
          style={{
            aspectRatio,
          }}
        >
          <span>Oops! Missing or broken video URL.</span>
        </div>
      )}

      {/* Video Navigation Controls (Mobile Only) */}
      <div
        id="video-player-controls"
        className="mt-4 flex flex-wrap items-center justify-between lg:hidden"
      >
        <button
          onClick={onPreviousVideo}
          // disable the button when it's the first video
          disabled={currentVideoNumber === 1}
          className={cx(
            "text-button-1 text-primary-on-dark flex cursor-pointer items-center gap-2 px-3 py-2",

            // disable state styles
            {
              "pointer-events-none cursor-none opacity-40":
                currentVideoNumber === 1,
            },
          )}
        >
          <PreviousIcon className="h-5 w-5" />
          <span>Previous</span>
        </button>

        {showIndicator && (
          <span className="text-secondary-on-dark">
            {currentVideoNumber}/{totalVideos}
          </span>
        )}

        <button
          onClick={onNextVideo}
          // disable the button when it's the last video
          disabled={currentVideoNumber === totalVideos}
          className={cx(
            "text-button-1 text-primary-on-dark flex cursor-pointer items-center gap-2 px-3 py-2",

            // disable state styles
            {
              "pointer-events-none cursor-none opacity-40":
                currentVideoNumber === totalVideos,
            },
          )}
        >
          <span>Next</span>
          <NextIcon className="h-5 w-5" />
        </button>
      </div>

      {/* Video Context: Title, Description, Tags, Metadata */}
      <div id="video-player-context" className="my-4">
        {/* Title */}
        <Link to={articlePath || ""}>
          <h1
            className={cx(
              "text-header-2 text-primary-on-dark hover:text-link-hover mb-4",
              titleClampLines ? `line-clamp-${titleClampLines}` : "",
            )}
          >
            {title}
          </h1>
        </Link>

        {/* Description */}
        <p className="mb-4">
          {/* Mobile: Read More Description */}
          {description && (
            <Fragment>
              <span
                ref={descriptionRef}
                className={cx(
                  "text-body-2 text-secondary-on-dark mt-3 lg:hidden",
                  isDescriptionExpanded ? "line-clamp-none" : "line-clamp-3",
                )}
              >
                {description}
              </span>

              {isDescriptionClamped && (
                <button
                  onClick={toggleDescriptionExpansion}
                  className={cx(
                    "text-secondary-on-dark cursor-pointer font-bold underline lg:hidden",
                    isDescriptionExpanded ? "mt-4 ml-0 block" : "",
                  )}
                >
                  {isDescriptionExpanded ? "less" : "more"}
                </button>
              )}
            </Fragment>
          )}

          {/* Desktop: Read More Description */}
          <span className="text-body-1 text-secondary-on-dark mt-3 hidden lg:inline">
            {isDescriptionExpanded || !description || description.length <= 200
              ? description
              : `${description.substring(0, 200)}...`}
            {description && description.length > 200 && (
              <button
                onClick={toggleDescriptionExpansion}
                className={cx(
                  "text-secondary-on-dark cursor-pointer font-bold underline",
                  isDescriptionExpanded ? "mt-4 ml-0 block" : "ml-1",
                )}
              >
                {isDescriptionExpanded ? "read less" : "read more"}
              </button>
            )}
          </span>
        </p>

        {/* Tags: For future usage */}
        {/* <div className="mb-4">
          {tags.length > 0 && (
            <div className="mt-4 flex flex-wrap gap-3">
              {tags.map((tag) => (
                <Button
                  size="medium"
                  variant="secondary"
                  key={tag.name + tag.id}
                >
                  <Link to={tag.urlPath || ""}>{tag.name}</Link>
                </Button>
              ))}
            </div>
          )}
        </div> */}

        {/* Metadata */}
        <p className="text-label-2 text-secondary-on-dark mt-4 flex flex-wrap font-bold">
          {/* View Count: For future usage */}
          {/* <span className="mr-6">{views} views</span> */}

          <ClientOnly>
            <span>
              <span className="mr-2">{publishedDate}</span>
              {/* <span className="mr-2">{publishedTime}</span> */}
            </span>
          </ClientOnly>
        </p>
      </div>

      {/* Author & Social Share */}
      <div
        id="video-player-author-share"
        className="grid grid-cols-4 gap-4 lg:grid-cols-12"
      >
        <div className="col-span-3 lg:col-span-4">
          {authors.map((author) => {
            return <Byline author={author} key={author.id} className="mt-3" />;
          })}
        </div>

        <div className="col-span-1 flex items-start justify-end gap-3 lg:col-span-8">
          <Button
            variant="tertiary"
            size="medium"
            icon={<GoogleIcon />}
            iconPosition="left"
            className="xl:text-button-1 hidden h-8 md:text-xs lg:flex"
          >
            <Link to="https://www.google.com/preferences/source?q=https://www.thinkchina.sg">
              Add ThinkChina as a preferred source
            </Link>
          </Button>

          <SocialShare
            isVideoArticle
            youtubeTitle={title}
            articleTitle={videoArticle.title}
            articleUrl={videoArticle.urlPath}
            className="col-span-1 flex-none self-start justify-self-end"
            viewType={isMobileOrTablet ? "dropdown" : "inline"}
            theme="dark"
            // enabledCopyLink={false}
          />
        </div>
      </div>

      <Button
        variant="tertiary"
        size="medium"
        icon={<GoogleIcon />}
        iconPosition="left"
        className="text-2sm mt-4 h-8 lg:hidden"
      >
        <Link to="https://www.google.com/preferences/source?q=https://www.thinkchina.sg">
          Add ThinkChina as a preferred source
        </Link>
      </Button>
    </div>
  );
}
